import styled, { keyframes } from 'styled-components'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`

const expand = keyframes`
  from { opacity: 0; max-height: 0; }
  to   { opacity: 1; max-height: 2000px; }
`

/* ---------- overlay + caixa -------------------------------------------------- */
export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  background: rgba(20, 10, 40, 0.5);

  @media (max-width: 768px) { padding: 10px; }

  @media print {
    position: static;
    background: none;
    padding: 0;
    overflow: visible;
  }
`

export const ModalCard = styled.div`
  width: 100%;
  max-width: 900px;
  background: var(--surface);
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  /* sem overflow:hidden — quebraria o position:sticky do cabeçalho */
  animation: ${fadeIn} 0.25s ease both;

  @media print {
    max-width: 100%;
    box-shadow: none;
    border-radius: 0;
    animation: none;
  }
`

/* ---------- corpo ------------------------------------------------------------ */
export const Body = styled.div`
  padding: 16px 24px 24px;
  text-align: center;

  @media (max-width: 768px) { padding: 12px; }
`

export const Loading = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--text, var(--text-muted));
`

export const Empty = styled.div`
  padding: 44px 16px;
  text-align: center;
  color: var(--text, var(--text-muted));
  font-size: 15px;
`

/* ---------- título + identificação ------------------------------------------ */
export const ContLogo = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-bottom: 16px;

  h2 { margin: 0; font-size: 1.9em; color: var(--text, var(--text-muted)); }

  @media (max-width: 600px) {
    padding-bottom: 10px;
    h2 { font-size: 1.35em; }
  }
`

export const Preview = styled.img`
  position: absolute;
  left: 0;
  width: 100px;
  height: 100px;
  object-fit: contain;

  @media (max-width: 600px) { width: 58px; height: 58px; }
`

export const YearBar = styled.div`
  background-color: #007bff;
  color: #fff;
  padding: 6px;
  border-radius: 8px;

  h3 { margin: 0; font-size: 1.3em; }
`

export const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 3px;
  padding: 12px 6px;
  width: 100%;

  span { font-size: 13.5px; color: var(--text, var(--text-muted)); }
  strong { color: #a06cf0; margin-right: 4px; }

  @media (max-width: 768px) { font-size: 12.5px; }
`

/* ---------- card colapsável de cada período --------------------------------- */
export const FormBlock = styled.section`
  width: 100%;
  margin-top: 12px;
  border: 1px solid #a06cf0;
  border-radius: 10px;
  background: var(--surface);
  text-align: left;
`

/* cabeçalho clicável — gruda no topo enquanto o relatório aberto rola */
export const FormHeader = styled.button`
  position: sticky;
  top: 0;
  z-index: 5;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  background: ${({ $open }) => ($open ? '#a06cf0' : '#f7f4fb')};
  color: ${({ $open }) => ($open ? 'var(--surface)' : '#333')};
  border: none;
  border-radius: ${({ $open }) => ($open ? '9px 9px 0 0' : '9px')};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.18s, color 0.18s;
  box-shadow: ${({ $open }) => ($open ? '0 2px 8px rgba(106,13,173,.28)' : 'none')};

  &:hover {
    background: ${({ $open }) => ($open ? '#5c0b96' : '#efe9f7')};
  }

  @media (max-width: 768px) { padding: 10px; }

  @media print {
    position: static;
    box-shadow: none;
    background: #a06cf0;
    color: #fff;
  }
`

export const FormHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`

export const FormPeriod = styled.span`
  font-size: 16px;
  font-weight: 700;
`

export const FormMeta = styled.span`
  margin-top: 6px;
  font-size: 12.5px;
  opacity: 0.85;
  font-weight: 500;
`

/* seta que gira ao expandir */
export const Chevron = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-size: 15px;
  line-height: 1;
  transition: transform 0.22s ease;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});

  @media print { display: none; }
`

/* corpo do relatório (só quando expandido) */
export const FormBody = styled.div`
  padding: 14px 16px 16px;
  border-top: 1px solid var(--line);
  animation: ${expand} 0.22s ease both;

  @media (max-width: 768px) { padding: 12px 10px; }
`

/* professores / turma / data — sempre visíveis no topo do card */
export const FormPeople = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 3px 16px;
  margin-top: 5px;
  font-size: 13.5px;
  font-weight: 500;
  color: ${({ $open }) => ($open ? 'rgba(255,255,255,.95)' : '#555')};

  strong {
    margin-right: 3px;
    font-weight: 700;
    color: ${({ $open }) => ($open ? 'var(--surface)' : '#a06cf0')};
  }
`

/* prévia do relatório — corta em 3 linhas quando colapsado */
export const FormPreview = styled.span`
  margin-top: 8px;
  font-size: 14.5px;
  font-weight: 400;
  line-height: 1.55;
  color: var(--text, var(--text-muted));
  white-space: pre-line;
  word-break: break-word;

  /* corte em exatamente 3 linhas com reticências */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

/* corpo expandido — recebe o HTML sanitizado do editor rich text */
export const FormRich = styled.div`
  margin: 0;
  line-height: 1.65;
  font-size: 15px;
  color: var(--text, var(--text-muted));
  text-align: left;
  word-break: break-word;

  /* estilos para as tags que vêm do editor */
  p { margin: 0 0 10px; }
  p:last-child { margin-bottom: 0; }
  strong, b { font-weight: 700; }
  em, i { font-style: italic; }
  u { text-decoration: underline; }
  ul, ol { margin: 0 0 10px; padding-left: 22px; }
  li { margin-bottom: 4px; }
  h1, h2, h3, h4, h5, h6 { margin: 0 0 8px; font-size: 1.05em; color: #a06cf0; }
  blockquote {
    margin: 0 0 10px;
    padding-left: 12px;
    border-left: 3px solid #e6e0ef;
    color: var(--text, var(--text-muted));
  }
`
export const TopBar = styled.div`
display: flex;
align-items: center;
justify-content: space-between;
gap: 10px;
padding: 12px 16px;
border-bottom: 1px solid var(--line);

@media print { display: none; }
`

export const CloseButton = styled.button`
     display: inline-flex;
     align-items: center;
     justify-content: center;
     width: 34px;
     height: 34px;
     border: 1px solid var(--line);
     border-radius: 8px;
     background: var(--surface);
     color: var(--text, var(--text-muted));
     cursor: pointer;
     transition: background 0.15s;
   
     &:hover { background: var(--surface); }
   `